import { useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, Globe, Code, Palette, Rocket, Shield, Clock, CheckCircle, ArrowRight, Zap, Layers, HeadphonesIcon, Star } from "lucide-react";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

const appPackages = [
  { id: "app_starter", price: 2499, timeline: "2-4 weeks", color: "#A0A0AB",
    name_en: "Starter App", name_es: "App Básica",
    desc_en: "Perfect for MVPs and simple apps", desc_es: "Perfecto para MVPs y apps simples",
    features_en: ["Single-platform app (iOS or Android)","Up to 5 core screens","Basic UI/UX design","User authentication","Push notifications","App Store / Play Store submission","30 days post-launch support"],
    features_es: ["App de una plataforma (iOS o Android)","Hasta 5 pantallas principales","Diseño UI/UX básico","Autenticación de usuarios","Notificaciones push","Envío a App Store / Play Store","30 días de soporte post-lanzamiento"],
  },
  { id: "app_pro", price: 5999, timeline: "4-8 weeks", color: "#00F0FF", popular: true,
    name_en: "Professional App", name_es: "App Profesional",
    desc_en: "Full-featured apps for growing businesses", desc_es: "Apps completas para negocios en crecimiento",
    features_en: ["Cross-platform (iOS + Android)","Up to 15 screens","Custom UI/UX design","User auth + social login","API integrations (payments, maps, etc.)","Admin dashboard","Push notifications + in-app messaging","Analytics integration","App Store + Play Store submission","60 days post-launch support"],
    features_es: ["Multiplataforma (iOS + Android)","Hasta 15 pantallas","Diseño UI/UX personalizado","Auth + inicio social","Integraciones API (pagos, mapas, etc.)","Panel de administración","Notificaciones push + mensajería in-app","Integración de analíticas","Envío a App Store + Play Store","60 días de soporte post-lanzamiento"],
  },
  { id: "app_enterprise", price: 14999, timeline: "8-16 weeks", color: "#FF007F",
    name_en: "Enterprise App", name_es: "App Empresarial",
    desc_en: "Complex, scalable applications", desc_es: "Aplicaciones complejas y escalables",
    features_en: ["Cross-platform (iOS + Android + Web)","Unlimited screens","Premium UI/UX design + branding","Advanced auth + role management","AI/ML integration","Real-time features (chat, notifications)","Payment processing (Stripe, etc.)","HIPAA / SOC2 compliance ready","Custom admin panel","CI/CD pipeline setup","All store submissions","90 days post-launch support + SLA"],
    features_es: ["Multiplataforma (iOS + Android + Web)","Pantallas ilimitadas","Diseño UI/UX premium + marca","Auth avanzada + gestión de roles","Integración AI/ML","Funciones en tiempo real (chat, notificaciones)","Procesamiento de pagos (Stripe, etc.)","Preparado para HIPAA / SOC2","Panel admin personalizado","Configuración de CI/CD","Envío a todas las tiendas","90 días de soporte + SLA"],
  },
];

const webPackages = [
  { id: "web_starter", price: 999, timeline: "1-2 weeks", color: "#A0A0AB",
    name_en: "Business Website", name_es: "Sitio Web Empresarial",
    desc_en: "Professional web presence", desc_es: "Presencia web profesional",
    features_en: ["Up to 5 pages","Responsive design (mobile + desktop)","Contact form","SEO optimization","Google Analytics setup","SSL certificate","Domain setup assistance","30 days support"],
    features_es: ["Hasta 5 páginas","Diseño responsive (móvil + escritorio)","Formulario de contacto","Optimización SEO","Configuración de Google Analytics","Certificado SSL","Asistencia con dominio","30 días de soporte"],
  },
  { id: "web_pro", price: 2999, timeline: "2-4 weeks", color: "#00F0FF", popular: true,
    name_en: "Advanced Website", name_es: "Sitio Web Avanzado",
    desc_en: "Dynamic sites with CMS and integrations", desc_es: "Sitios dinámicos con CMS e integraciones",
    features_en: ["Up to 15 pages","Custom design + animations","Content management system (CMS)","Blog / News section","Email integration","Social media integration","Advanced SEO + Open Graph","Speed optimization","60 days support"],
    features_es: ["Hasta 15 páginas","Diseño personalizado + animaciones","Sistema de gestión de contenido (CMS)","Sección de blog / noticias","Integración de email","Integración de redes sociales","SEO avanzado + Open Graph","Optimización de velocidad","60 días de soporte"],
  },
  { id: "web_ecommerce", price: 7999, timeline: "4-8 weeks", color: "#FF007F",
    name_en: "E-Commerce / Marketplace", name_es: "E-Commerce / Tienda Digital",
    desc_en: "Full online store or marketplace", desc_es: "Tienda en línea completa o marketplace",
    features_en: ["Unlimited pages","Premium custom design","Product catalog + inventory","Shopping cart + checkout","Payment processing (Stripe)","User accounts + dashboards","Order management system","Email notifications","Analytics dashboard","90 days support + SLA"],
    features_es: ["Páginas ilimitadas","Diseño premium personalizado","Catálogo de productos + inventario","Carrito de compras + checkout","Procesamiento de pagos (Stripe)","Cuentas de usuario + paneles","Sistema de gestión de pedidos","Notificaciones por email","Panel de analíticas","90 días de soporte + SLA"],
  },
];

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState("apps");
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState("idle");
  const { t, lang } = useLang();

  const handleQuoteRequest = async (e) => {
    e.preventDefault();
    setFormStatus("sending");
    try {
      await api.submitContact({ ...contactForm, message: `[SERVICE INQUIRY] ${contactForm.message}` });
      setFormStatus("sent");
      setContactForm({ name: "", email: "", message: "" });
      setTimeout(() => setFormStatus("idle"), 5000);
    } catch { setFormStatus("error"); setTimeout(() => setFormStatus("idle"), 3000); }
  };

  const packages = activeTab === "apps" ? appPackages : webPackages;
  const l = (pkg, field) => pkg[`${field}_${lang}`] || pkg[`${field}_en`];

  const addOns = [
    { name: t("svc_addon_logo"), price: 499, icon: Palette },
    { name: t("svc_addon_maint"), price: 299, period: lang === "es" ? "/mes" : "/mo", icon: Shield },
    { name: t("svc_addon_ai"), price: 999, icon: Zap },
    { name: t("svc_addon_api"), price: 1499, icon: Code },
    { name: t("svc_addon_aso"), price: 399, icon: Rocket },
    { name: t("svc_addon_support"), price: 499, period: lang === "es" ? "/mes" : "/mo", icon: HeadphonesIcon },
  ];

  const steps = [
    { step: "01", title: t("svc_step1"), desc: t("svc_step1_desc") },
    { step: "02", title: t("svc_step2"), desc: t("svc_step2_desc") },
    { step: "03", title: t("svc_step3"), desc: t("svc_step3_desc") },
    { step: "04", title: t("svc_step4"), desc: t("svc_step4_desc") },
  ];

  const whyUs = [
    { icon: Shield, title: t("svc_why_1"), desc: t("svc_why_1d") },
    { icon: Rocket, title: t("svc_why_2"), desc: t("svc_why_2d") },
    { icon: Layers, title: t("svc_why_3"), desc: t("svc_why_3d") },
    { icon: Star, title: t("svc_why_4"), desc: t("svc_why_4d") },
  ];

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="services-page">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">

          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
            <p className="label-mono mb-4">{t("svc_label")}</p>
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6">
              {t("svc_heading_1")} <br />
              <span className="bg-gradient-to-r from-[#00F0FF] via-[#4D00FF] to-[#FF007F] bg-clip-text text-transparent">{t("svc_heading_2")}</span>
            </h1>
            <p className="text-[#A0A0AB] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">{t("svc_subtitle")}</p>
          </motion.div>

          {/* Tab Toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex gap-2 p-1.5 bg-white/5 rounded-2xl border border-white/10">
              <button data-testid="services-tab-apps" onClick={() => setActiveTab("apps")}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${activeTab === "apps" ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" : "text-[#A0A0AB]"}`}>
                <Smartphone className="w-4 h-4" /> {t("svc_tab_app")}
              </button>
              <button data-testid="services-tab-web" onClick={() => setActiveTab("web")}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all ${activeTab === "web" ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" : "text-[#A0A0AB]"}`}>
                <Globe className="w-4 h-4" /> {t("svc_tab_web")}
              </button>
            </div>
          </div>

          {/* Packages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            {packages.map((pkg, i) => (
              <motion.div key={pkg.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                data-testid={`service-pkg-${pkg.id}`}
                className={`glass-card rounded-3xl p-7 relative flex flex-col ${pkg.popular ? "border-[#00F0FF]/40 shadow-[0_0_30px_rgba(0,240,255,0.1)]" : ""}`}>
                {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-[10px] font-bold px-4 py-1 rounded-full">{lang === "es" ? "MÁS POPULAR" : "MOST POPULAR"}</div>}
                <div className="mb-6">
                  <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-1">{l(pkg, "name")}</h3>
                  <p className="text-[#A0A0AB] text-xs">{l(pkg, "desc")}</p>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-black text-white">${pkg.price.toLocaleString()}</span>
                  <span className="text-[#A0A0AB] text-sm ml-1">{t("svc_starting")}</span>
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-4 h-4" style={{ color: pkg.color }} />
                  <span className="text-sm" style={{ color: pkg.color }}>{pkg.timeline}</span>
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {l(pkg, "features").map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: pkg.color }} />
                      <span className="text-[#A0A0AB]">{f}</span>
                    </li>
                  ))}
                </ul>
                <a href="/services/pricing" data-testid={`service-cta-${pkg.id}`}
                  onClick={(e) => { e.preventDefault(); window.location.href = "/services/pricing"; }}
                  className={`block text-center py-3.5 rounded-full text-sm font-semibold transition-all ${pkg.popular ? "bg-[#00F0FF] text-black hover:shadow-[0_0_25px_rgba(0,240,255,0.6)]" : "border border-white/20 text-white hover:border-white/40"}`}>
                  {t("svc_get_started")} <ArrowRight className="w-4 h-4 inline ml-1" />
                </a>
              </motion.div>
            ))}
          </div>

          {/* Add-Ons */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20">
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold text-white text-center mb-3">{t("svc_addons")}</h2>
            <p className="text-[#A0A0AB] text-sm text-center mb-10">{t("svc_addons_sub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {addOns.map((addon, i) => (
                <motion.div key={addon.name} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="glass-card rounded-2xl p-5 flex items-center gap-4 hover:-translate-y-1 transition-all duration-300 hover:border-[#00F0FF]/20">
                  <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center shrink-0"><addon.icon className="w-5 h-5 text-[#00F0FF]" /></div>
                  <div className="flex-1"><p className="text-white font-semibold text-sm">{addon.name}</p></div>
                  <div className="text-right shrink-0">
                    <p className="text-white font-bold">${addon.price.toLocaleString()}</p>
                    {addon.period && <p className="text-[#A0A0AB] text-xs">{addon.period}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Process */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20">
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold text-white text-center mb-3">{t("svc_process")}</h2>
            <p className="text-[#A0A0AB] text-sm text-center mb-10">{t("svc_process_sub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((s, i) => (
                <motion.div key={s.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative">
                  <div className="glass-card rounded-2xl p-6">
                    <span className="font-['Unbounded'] text-3xl font-black bg-gradient-to-r from-[#00F0FF] to-[#4D00FF] bg-clip-text text-transparent">{s.step}</span>
                    <h3 className="font-['Unbounded'] text-base font-bold text-white mt-3 mb-2">{s.title}</h3>
                    <p className="text-[#A0A0AB] text-xs leading-relaxed">{s.desc}</p>
                  </div>
                  {i < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 text-[#00F0FF]/30"><ArrowRight className="w-6 h-6" /></div>}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Why Us */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20">
            <div className="glass-card rounded-3xl p-8 sm:p-10">
              <h2 className="font-['Unbounded'] text-2xl font-bold text-white text-center mb-8">{t("svc_why")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {whyUs.map((item) => (
                  <div key={item.title} className="text-center">
                    <div className="w-12 h-12 rounded-2xl bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4"><item.icon className="w-6 h-6 text-[#00F0FF]" /></div>
                    <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-[#A0A0AB] text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Quote Form */}
          <motion.div id="quote" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="max-w-2xl mx-auto">
              <h2 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold text-white text-center mb-3">{t("svc_quote")}</h2>
              <p className="text-[#A0A0AB] text-sm text-center mb-8">{t("svc_quote_sub")}</p>
              <form onSubmit={handleQuoteRequest} className="glass-card rounded-3xl p-8" data-testid="quote-form">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm text-[#A0A0AB] mb-2">{t("contact_name")}</label>
                    <input data-testid="quote-name" type="text" required value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all" placeholder={t("contact_name_ph")} />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A0A0AB] mb-2">{t("contact_email")}</label>
                    <input data-testid="quote-email" type="email" required value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all" placeholder={t("contact_email_ph")} />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A0A0AB] mb-2">{t("contact_message")}</label>
                    <textarea data-testid="quote-message" required rows={5} value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all resize-none" placeholder={t("svc_quote_msg_ph")} />
                  </div>
                  <button data-testid="quote-submit" type="submit" disabled={formStatus === "sending"} className="neon-btn w-full py-4 text-base disabled:opacity-50">
                    {formStatus === "sending" ? t("contact_sending") : formStatus === "sent" ? t("contact_sent") : t("svc_quote_submit")}
                  </button>
                  {formStatus === "error" && <p className="text-red-400 text-sm text-center">{t("contact_error")}</p>}
                </div>
              </form>
            </div>
          </motion.div>

        </div>
      </div>
      <Footer />
    </div>
  );
}
